import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, BookOpen, CheckCircle2, ScrollText, PlayCircle } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import { CompletedDocumentViewer } from './CompletedDocumentViewer';
import { resolveUnaForUser, renderMergeFields, createMergeData } from '@/lib/documentMerge';
import { sanitizeDocumentContent } from '@/lib/sanitize';

interface JourneyReadingStepProps {
  stepId: string;
  userId: string;
  contentTemplate: string;
  acknowledgmentText: string;
  videoUrl?: string;
  isCompleted: boolean;
  onComplete: () => void;
  initialProgress?: number;
  videoIntroText?: string;
  readingIntroText?: string;
  stepTitle?: string;
  completedDocumentId?: string;
  completedDescriptionText?: string;
}

export function JourneyReadingStep({
  stepId,
  userId,
  contentTemplate,
  acknowledgmentText,
  videoUrl,
  isCompleted,
  onComplete,
  initialProgress = 0,
  videoIntroText,
  readingIntroText,
  stepTitle = 'Document',
  completedDocumentId,
  completedDescriptionText,
}: JourneyReadingStepProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [templateContent, setTemplateContent] = useState<string>('');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unaName, setUnaName] = useState<string>('');
  const [videoWatchedPercent, setVideoWatchedPercent] = useState(initialProgress);
  const [videoCompleted, setVideoCompleted] = useState(!videoUrl || initialProgress >= 90);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Refs to track latest values for cleanup
  const videoProgressRef = useRef(initialProgress);
  const lastSavedPercentRef = useRef(Math.floor(initialProgress / 5));
  const isCompletedRef = useRef(false);

  // Update ref when progress changes
  useEffect(() => {
    videoProgressRef.current = videoWatchedPercent;
  }, [videoWatchedPercent]);

  // Save progress on component unmount
  useEffect(() => {
    return () => {
      // Don't save progress if step was just completed - avoid race condition
      if (isCompletedRef.current) return;
      
      const currentProgress = videoProgressRef.current;
      if (currentProgress > 0 && currentProgress > initialProgress) {
        supabase
          .from('user_journey_step_progress')
          .upsert({
            user_id: userId,
            step_id: stepId,
            status: 'active',
            action_data: { watch_percent: currentProgress },
          }, { onConflict: 'user_id,step_id' })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
          });
      }
    };
  }, [userId, stepId, initialProgress, queryClient]);

  // Fetch template content
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Resolve UNA for user (treasury secretary OR member)
        const unaContext = await resolveUnaForUser(userId);
        
        if (unaContext) {
          setUnaName(unaContext.unaName);
        }

        // Fetch document template
        const { data: template, error: templateError } = await supabase
          .from('document_templates')
          .select('template_content')
          .eq('template_name', contentTemplate)
          .eq('is_active', true)
          .maybeSingle();

        if (templateError) {
          console.error('Error fetching template:', templateError);
        }
        
        if (template) {
          // Render merge fields with UNA name
          const mergeData = createMergeData(unaContext?.unaName || '[Your UNA]');
          const content = renderMergeFields(template.template_content, mergeData);
          setTemplateContent(content);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, contentTemplate]);

  // Track scroll position
  const handleScroll = () => {
    if (!contentRef.current || !videoCompleted) return;
    
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold
    
    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleVideoProgress = async (percent: number) => {
    setVideoWatchedPercent(percent);
    
    const currentInterval = Math.floor(percent / 5);
    if (currentInterval > lastSavedPercentRef.current) {
      lastSavedPercentRef.current = currentInterval;
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'active',
          action_data: { watch_percent: percent },
        }, { onConflict: 'user_id,step_id' });
      
      queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
    }
  };

  const handleVideoComplete = () => {
    setVideoCompleted(true);
  };

  const handleAcknowledge = async () => {
    if (!isAcknowledged) return;
    
    setIsSubmitting(true);
    // Prevent cleanup from overwriting completion
    isCompletedRef.current = true;
    
    try {
      // Get user's UNA (treasury secretary OR member)
      const unaContext = await resolveUnaForUser(userId);

      let documentId: string | null = null;

      // Create a generated_documents record for this reading
      if (unaContext && templateContent) {
        const { data: docRecord, error: docError } = await supabase
          .from('generated_documents')
          .insert({
            una_id: unaContext.unaId,
            document_name: stepTitle,
            document_type: 'reading_acknowledgment',
            custom_content: templateContent,
            status: 'finalized',
            finalized_at: new Date().toISOString(),
            created_by: userId,
          })
          .select('id')
          .single();

        if (!docError && docRecord) {
          documentId = docRecord.id;

          // File to governance_documents
          await supabase
            .from('governance_documents')
            .insert({
              una_id: unaContext.unaId,
              document_type: 'foundational_reading',
              document_category: 'foundational_documents',
              document_title: stepTitle,
              content_html: templateContent,
              generated_document_id: documentId,
              access_level: 'all_members',
              status: 'active',
            });
        }
      }

      // Mark step as completed with document reference
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          action_data: {
            acknowledged_at: new Date().toISOString(),
            document_id: documentId,
          },
        }, { onConflict: 'user_id,step_id' });

      // Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      toast.success('Reading acknowledged and filed to governance');
      onComplete();
    } catch (error) {
      console.error('Error acknowledging:', error);
      toast.error('Failed to save acknowledgment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="space-y-6">
        {/* VIDEO FIRST - on top */}
        {videoUrl && (
          <div className="space-y-3">
            <div className="relative">
              <VideoPlayer
                videoUrl={videoUrl}
                requiredWatchPercent={0}
                onProgress={() => {}}
                onComplete={() => {}}
                initialProgress={0}
              />
              <div className="absolute top-3 right-3 bg-green-600/90 text-white px-3 py-1 rounded-full flex items-center gap-1.5 text-sm font-medium shadow-lg pointer-events-none">
                <CheckCircle2 className="w-4 h-4" />
                <span>Completed</span>
              </div>
            </div>
            
            {/* Description text BELOW video */}
            {completedDescriptionText && (
              <div className="p-4 bg-amber-50/50 border border-amber-200/50 rounded-lg">
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {completedDescriptionText}
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Show the completed document */}
        <CompletedDocumentViewer
          documentId={completedDocumentId || ''}
          documentTitle={stepTitle}
          successMessage="You have read this document, and it is now filed with your Association."
          templateName={contentTemplate}
          userId={userId}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Video Section */}
      {videoUrl && (
        <VideoPlayer
          videoUrl={videoUrl}
          requiredWatchPercent={90}
          onProgress={handleVideoProgress}
          onComplete={handleVideoComplete}
          initialProgress={initialProgress}
        />
      )}

      {/* Instructions - only show when video is complete or no video */}
      {videoCompleted && (
        <>
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <ScrollText className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Required Reading</p>
              <p className="text-sm text-blue-700">
                {readingIntroText || 'We will file this document for you in your UNA governance section. Which you\'ll be given access to after you complete the journey.'}
              </p>
            </div>
          </div>

          <Card>
            <CardContent className="pt-6">
              
              <div 
                ref={contentRef}
                onScroll={handleScroll}
                className="border rounded-lg p-6 max-h-[500px] overflow-y-auto ecclesiastical-document max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(templateContent) }}
              />
              
              {!hasScrolledToBottom && (
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  ↓ Scroll down to read the full content ↓
                </p>
              )}
            </CardContent>
          </Card>

          {/* Acknowledgment Section */}
          <div className="space-y-4">
            <div className={`flex items-start gap-3 p-4 rounded-lg border transition-colors ${
              hasScrolledToBottom 
                ? 'bg-background border-primary/20' 
                : 'bg-muted/50 border-muted opacity-60'
            }`}>
              <Checkbox
                id="acknowledge"
                checked={isAcknowledged}
                onCheckedChange={(checked) => setIsAcknowledged(checked as boolean)}
                disabled={!hasScrolledToBottom}
              />
              <label 
                htmlFor="acknowledge" 
                className={`text-sm cursor-pointer ${!hasScrolledToBottom ? 'cursor-not-allowed' : ''}`}
              >
                {acknowledgmentText}
              </label>
            </div>

            <Button
              onClick={handleAcknowledge}
              disabled={!isAcknowledged || isSubmitting}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Continue'
              )}
            </Button>
          </div>
        </>
      )}

      {/* Locked state when video not complete */}
      {videoUrl && !videoCompleted && (
        <div className="p-6 bg-muted/50 border rounded-lg text-center">
          <ScrollText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">
            Complete the video above to unlock the reading content
          </p>
        </div>
      )}
    </div>
  );
}
